import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  type Firestore,
} from 'firebase/firestore';

/**
 * Firebase wiring.
 *
 * Every value here is public by design — the web config identifies the
 * project, it does not authorise anything. Access is decided entirely by
 * firestore.rules.
 */

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
};

const ENV_NAMES: Record<keyof typeof config, string> = {
  apiKey: 'VITE_FIREBASE_API_KEY',
  authDomain: 'VITE_FIREBASE_AUTH_DOMAIN',
  projectId: 'VITE_FIREBASE_PROJECT_ID',
  storageBucket: 'VITE_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'VITE_FIREBASE_APP_ID',
};

/** Which required variables are still blank. Empty means good to go. */
export const missingConfigKeys: string[] = (Object.keys(config) as (keyof typeof config)[])
  .filter((key) => !config[key])
  .map((key) => ENV_NAMES[key]);

export const isFirebaseConfigured = missingConfigKeys.length === 0;

const useEmulators = import.meta.env.VITE_FIREBASE_EMULATORS === 'true';

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let firestoreInstance: Firestore | null = null;

function getApp(): FirebaseApp {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase is not configured. See .env.example.');
  }
  app ??= initializeApp(config);
  return app;
}

export function getAuthClient(): Auth {
  if (!authInstance) {
    /*
     * Persistence order matters, and the default order is wrong for Safari.
     *
     * `getAuth()` prefers IndexedDB. Safari closes IndexedDB connections while
     * a page is hidden — which is precisely what the app is while the Google
     * sign-in popup holds focus. The credential exchange succeeds, then
     * persisting the user fails with "Database is closing/hidden" and the whole
     * sign-in rejects, so a working login looks like a broken one.
     *
     * localStorage has no such behaviour, so it goes first and IndexedDB stays
     * as the fallback. Choosing persistence means using `initializeAuth`, which
     * in turn needs the popup/redirect resolver passed explicitly.
     */
    authInstance = initializeAuth(getApp(), {
      persistence: [browserLocalPersistence, indexedDBLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
    if (useEmulators) {
      connectAuthEmulator(authInstance, 'http://127.0.0.1:9099', { disableWarnings: true });
    }
  }
  return authInstance;
}

export function getDb(): Firestore {
  if (!firestoreInstance) {
    // A local cache is not offline support — the app shell still needs the
    // network — but it makes a warm start render the shelf instantly instead
    // of waiting on a round trip, which matters a lot on a phone.
    firestoreInstance = initializeFirestore(getApp(), {
      localCache: persistentLocalCache({ tabManager: persistentSingleTabManager({}) }),
    });
    if (useEmulators) {
      connectFirestoreEmulator(firestoreInstance, '127.0.0.1', 8080);
    }
  }
  return firestoreInstance;
}

export const COLLECTIONS = {
  movies: 'movies',
  watches: 'watches',
  wishlist: 'wishlist',
  settings: 'settings',
} as const;

/** The settings collection holds exactly one document. */
export const SETTINGS_DOC = 'app';
